import {
  Form,
  Image,
  Input,
  Row,
  Collapse,
  Upload,
  Typography,
  Spin,
  Button,
  App
} from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FULLY_SUPPORTED_LANGUAGES as LANGUAGES } from 'constants/languages';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useFileUpload } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { createBlog, fetchBlogById, updateBlog } from 'services/api/blog';
import { fetchSpecialties } from 'services/api/specialties';
import { BlogInput, BlogTranslationInput } from 'types/api/blog';
import { SpecialtyListItem } from 'types/api/specialties';

import { showErrorMessage } from 'utils/showErrorMessage';
import { CKEditor } from 'components/shared';
import { Loading, PageWrapper } from 'components/ui';
import { getInputDir } from 'utils/helpers';
import BlogTagsSelect from './components/BlogTagsSelect';
import SpecialtySelect from './components/SpecialtySelect';
import { splitSpecialtyRelations } from './utils';

import 'react-quill/dist/quill.snow.css';

const itemValues = {
  title: '',
  body: '',
  short_desc: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  tags: [] as number[]
};

type ValuesType = typeof itemValues;

/**
 * Form shape: the per-locale tab values live under their locale key
 * (`en`, `ru`, `de`), everything else is a top-level field. `symptoms`
 * and `specialties` are separate pickers here but merge into one
 * `specialties` array on submit — the BE splits them by catalog `type`.
 */
type BodyType = {
  slug?: string;
  symptoms?: number[];
  specialties?: number[];
  [locale: string]: string | number[] | ValuesType | undefined;
};

const { Panel } = Collapse;
const { Text } = Typography;

const BlogNewsDetails: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const params = useParams();
  const { modal } = App.useApp();
  const blogId = params?.id;
  //AWS
  const {
    fileUrl: file,
    loading: isUploading,
    result: fileValue,
    setResult,
    upload
  } = useFileUpload({
    handleResult: true,
    loading: true
  });

  const [isFetching, setFetching] = useState(!!blogId);
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Tag ids that came back on the blog but could not be attributed to one
   * of the locale tabs (no `lang` on the relation, or a locale we don't
   * edit here — e.g. `ar`/`bg`, which are in LANGUAGES but not fully
   * supported). Saving replaces the whole `tags` array, so these are
   * carried through untouched instead of being silently unlinked.
   */
  const [orphanTagIds, setOrphanTagIds] = useState<number[]>([]);

  const [symptomOptions, setSymptomOptions] = useState<SpecialtyListItem[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<SpecialtyListItem[]>(
    []
  );

  const showSuccess = (text: string) => {
    modal.success({
      title: 'Success',
      content: text,
      onOk() {
        navigate('/blog-news');
      },
      onCancel() {
        navigate('/blog-news');
      }
    });
  };

  useEffect(() => {
    if (!blogId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setFetching(true);

        // Fetched alongside the blog because the prefill needs the symptom
        // id set to decide which picker each attached relation belongs in.
        //
        // The specialty half is derived as "everything minus the symptoms"
        // rather than requested with `symptom: false` — only `symptom:
        // true` is known to be honoured by the BE (it's what the
        // Specialties page uses), and this stays correct whether the
        // unfiltered list includes symptoms or not.
        const [res, symptomCatalog, fullCatalog] = await Promise.all([
          fetchBlogById(blogId),
          fetchSpecialties({ lang: 'en', symptom: true }),
          fetchSpecialties({ lang: 'en' })
        ]);

        if (cancelled || !res.data) return;

        const symptomCatalogIds = new Set(symptomCatalog.map(item => item.id));

        setSymptomOptions(symptomCatalog);
        setSpecialtyOptions(
          fullCatalog.filter(item => !symptomCatalogIds.has(item.id))
        );

        const { image, slug, translates, specialties, tags } = res.data;

        // Fan the flat `tags` array back out to the locale tabs it came
        // from. Anything we can't place is stashed in `orphanTagIds` and
        // re-sent on save (see the state declaration).
        const editableLocales = new Set<string>(
          LANGUAGES.map(item => item.locale)
        );

        const tagsByLocale: Record<string, number[]> = {};
        const orphans: number[] = [];

        for (const tag of tags || []) {
          if (tag.lang && editableLocales.has(tag.lang)) {
            tagsByLocale[tag.lang] = [
              ...(tagsByLocale[tag.lang] || []),
              tag.id
            ];
          } else {
            orphans.push(tag.id);
          }
        }

        setOrphanTagIds(orphans);

        const values = (translates || []).reduce<Record<string, ValuesType>>(
          (acc, val) => {
            const keywords = val.keywords || {};

            return {
              ...acc,
              [val.lang]: {
                title: val.title ?? '',
                body: val.body ?? '',
                short_desc: val.short_desc ?? '',
                meta_title: keywords.title ?? '',
                meta_description: keywords.description ?? '',
                meta_keywords: keywords.keywords ?? '',
                tags: tagsByLocale[val.lang] || []
              }
            };
          },
          {}
        );

        // A locale can have tags without having a translation row yet, so
        // seed any locale tab the reduce above didn't reach.
        for (const [lang, tagIds] of Object.entries(tagsByLocale)) {
          if (!values[lang]) {
            values[lang] = { ...itemValues, tags: tagIds };
          }
        }

        // Symptoms and specialties arrive as one array — fan it back out
        // into the two pickers so each relation shows as a removable chip.
        const attached = splitSpecialtyRelations(
          specialties,
          symptomCatalogIds
        );

        setResult(image || '');
        form.setFieldsValue({
          slug,
          symptoms: attached.symptoms,
          specialties: attached.specialties,
          ...values
        });
      } catch {
        // swallow — form stays empty on failure
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [blogId, form, setResult]);

  const onFinish = useCallback(
    async (values: BodyType) => {
      setErrorKeys([]);

      // Build the per-locale translations array. Form values arrive as
      // {slug, symptoms, specialties, en: {...}, ru: {...}, ...} — the
      // non-locale fields are pulled out by name first, so whatever is
      // left over is a locale tab. Cycle-8 BE expects
      // `translations: [{lang, title, body, short_desc, keywords}]`
      // — the upsert runs by (blog_id, lang).
      const { slug, symptoms = [], specialties = [], ...localeValues } = values;

      const translations: BlogTranslationInput[] = [];
      const tagIds: number[] = [...orphanTagIds];

      for (const [lang, val] of Object.entries(localeValues)) {
        if (!val || typeof val !== 'object' || Array.isArray(val)) continue;

        // `tags` is a locale-scoped picker but ships in one flat array,
        // so it's collected here rather than sent per translation.
        tagIds.push(...(val.tags || []));

        translations.push({
          lang,
          title: val.title,
          body: val.body,
          short_desc: val.short_desc,
          keywords: {
            title: val.meta_title,
            description: val.meta_description,
            keywords: val.meta_keywords
          }
        });
      }

      const input: BlogInput = {
        slug,
        image: fileValue,
        translations
      };

      if (blogId) {
        // Relations ride the update path only — the create endpoint
        // isn't confirmed to handle them, so on create the pickers are
        // hidden and the editor attaches them on the next save. `[]` is
        // meaningful here (it unlinks), so these are always sent on
        // update rather than omitted when empty.
        input.specialties = [...symptoms, ...specialties];
        input.tags = Array.from(new Set(tagIds));

        setIsUpdating(true);

        try {
          await updateBlog(blogId, input);
          showSuccess(
            'The blog has been successfully updated. Please note, it may take a little time to become available in the app.'
          );
        } catch (err) {
          showErrorMessage(err);
        } finally {
          setIsUpdating(false);
        }
      } else {
        setIsCreating(true);

        try {
          await createBlog(input);
          showSuccess(
            'Your blog has been successfully created. Please note, it may take a little time to become available in the app.'
          );
        } catch (err) {
          showErrorMessage(err);
        } finally {
          setIsCreating(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blogId, fileValue, orphanTagIds]
  );

  const onFinishError = (errorInfo: ValidateErrorEntity<BodyType>) => {
    setErrorKeys([]);
    const errors = new Set(
      errorInfo.errorFields.map(item => `${item.name[0]}`)
    );

    setErrorKeys(Array.from(errors));
  };

  return (
    <PageWrapper
      title={blogId ? 'Update' : 'Create'}
      extra={[
        <Button
          type="primary"
          key="save-blog"
          loading={isCreating || isUpdating}
          onClick={form.submit}
        >
          {t('common.save')}
        </Button>
      ]}
    >
      {isFetching ? (
        <Loading />
      ) : (
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishError}
          layout="vertical"
          size="large"
        >
          <Row justify="center" style={{ marginBottom: '--margin-md' }}>
            <Upload
              multiple={false}
              maxCount={1}
              showUploadList={false}
              customRequest={e => upload(e.file as File)}
            >
              {isUploading ? (
                <Spin />
              ) : (
                <Image
                  src={file}
                  width={300}
                  height={200}
                  preview={false}
                  style={{ objectFit: 'cover' }}
                />
              )}
            </Upload>
          </Row>
          <Form.Item
            name="slug"
            label={t('common.slug')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          {blogId ? (
            <>
              <Form.Item name="symptoms" label={t('blog.symptoms')}>
                <SpecialtySelect
                  options={symptomOptions}
                  placeholder={t('blog.select_symptoms')}
                />
              </Form.Item>
              <Form.Item name="specialties" label={t('blog.specialties')}>
                <SpecialtySelect
                  options={specialtyOptions}
                  placeholder={t('blog.select_specialties')}
                />
              </Form.Item>
            </>
          ) : (
            <Text type="secondary" style={{ display: 'block' }}>
              {t('blog.relations_hint')}
            </Text>
          )}
          <Collapse
            accordion
            defaultActiveKey={LANGUAGES[0].locale}
            items={LANGUAGES.map(item => {
              const hasError = errorKeys.includes(item.locale);

              return {
                key: item.key,
                label: item.label,
                forceRender: true,
                children: (
                  <Panel
                    header={
                      <Text
                        style={{
                          color: `var(--${
                            hasError ? 'error-color' : 'text-color'
                          })`
                        }}
                      >
                        {item.label}
                      </Text>
                    }
                    key={item.locale}
                    forceRender
                  >
                    <Form.Item
                      name={[item.locale, 'meta_title']}
                      label={t('blog.meta_title')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'meta_description']}
                      label={t('blog.meta_description')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'meta_keywords']}
                      label={t('blog.meta_keywords')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'title']}
                      label={t('blog.title')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'short_desc']}
                      label={t('blog.short_desc')}
                      rules={[{ required: true }]}
                    >
                      <Input dir={getInputDir(item.locale)} />
                    </Form.Item>
                    <Form.Item
                      name={[item.locale, 'body']}
                      label="Body"
                      rules={[{ required: true }]}
                    >
                      <CKEditor language={item.locale} />
                    </Form.Item>
                    {!!blogId && (
                      // Tags are per-language records, so each locale
                      // tab picks from (and creates in) its own set.
                      <Form.Item
                        name={[item.locale, 'tags']}
                        label={t('blog.tags')}
                      >
                        <BlogTagsSelect
                          language={item.locale}
                          placeholder={t('blog.select_or_create_tags')}
                        />
                      </Form.Item>
                    )}
                  </Panel>
                )
              };
            })}
          />
        </Form>
      )}
    </PageWrapper>
  );
};

export default BlogNewsDetails;
